import { LogOut } from 'lucide-react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useAuth } from '@/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/Logo'
import { ThemeToggle } from '@/components/theme-toggle'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ManageNav, showManageNav } from '@/components/ManageNav'
import { useInboxCount } from '@/platform/hooks/useInboxCount'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/** The one page width: the header and every page's content share it. */
const SHELL_WIDTH = 'mx-auto w-full max-w-[1600px] px-4 sm:px-6 lg:px-10'

function navClass(isActive: boolean) {
    return cn(
        'whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
        isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
    )
}

function initialsOf(name: string): string {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('') || 'U'
}

function AccountMenu() {
    const auth = useAuth()
    const name = auth.user?.name || auth.accountName || 'User'
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 pl-1.5">
                    <Avatar className="size-6">
                        {auth.photoUrl && <AvatarImage src={auth.photoUrl} alt={name} />}
                        <AvatarFallback className="bg-primary text-[10px] font-semibold text-primary-foreground">
                            {initialsOf(name)}
                        </AvatarFallback>
                    </Avatar>
                    <span className="hidden max-w-32 truncate sm:inline">{name}</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel className="flex flex-col">
                    <span className="truncate">{name}</span>
                    {auth.user?.email && (
                        <span className="truncate text-xs font-normal text-muted-foreground">{auth.user.email}</span>
                    )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {auth.mode === 'dev' ? (
                    <>
                        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                            Switch dev user
                        </DropdownMenuLabel>
                        <DropdownMenuRadioGroup value={auth.currentDevUserId} onValueChange={auth.switchDevUser}>
                            {auth.devUsers.map((u) => (
                                <DropdownMenuRadioItem key={u.id} value={u.id}>
                                    {u.name}
                                </DropdownMenuRadioItem>
                            ))}
                        </DropdownMenuRadioGroup>
                    </>
                ) : (
                    <DropdownMenuItem onClick={auth.logout}>
                        <LogOut className="size-4" /> Sign out
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export function AppShell({ children }: { children: React.ReactNode }) {
    const { user, mode } = useAuth()
    const pathname = useRouterState({ select: (s) => s.location.pathname })
    const inbox = useInboxCount(Boolean(user))
    return (
        <div className="flex min-h-screen flex-col bg-background">
            <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 print:hidden">
                <div className={cn(SHELL_WIDTH, 'flex h-14 items-center gap-2')}>
                    <Link to="/" className="flex items-center gap-2" aria-label="Home">
                        <Logo className="h-7" />
                        <span className="hidden whitespace-nowrap text-sm font-semibold text-foreground lg:inline">Risk Management</span>
                    </Link>
                    <nav className="ml-3 hidden items-center gap-1 sm:flex">
                        <Link to="/submissions" className={navClass(pathname.startsWith('/submissions'))}>My requests</Link>
                        <Link to="/inbox" className={cn(navClass(pathname === '/inbox'), 'flex items-center gap-1.5')}>
                            Approvals
                            {inbox > 0 && <Badge className="h-5 min-w-5 justify-center px-1 text-[10px]">{inbox}</Badge>}
                        </Link>
                        <Link to="/dashboards" className={navClass(pathname.startsWith('/dashboards'))}>Dashboards</Link>
                        {user?.isRiskManager && (
                            <Link to="/manage/forms" className={navClass(pathname.startsWith('/manage'))}>Manage</Link>
                        )}
                    </nav>
                    <div className="ml-auto flex items-center gap-1.5">
                        {mode === 'dev' && (
                            <Badge variant="outline" className="hidden border-amber-500/40 text-amber-600 sm:inline dark:text-amber-400">
                                Dev
                            </Badge>
                        )}
                        <ThemeToggle />
                        <AccountMenu />
                    </div>
                </div>
                {user?.isRiskManager && showManageNav(pathname) && <ManageNav pathname={pathname} widthClass={SHELL_WIDTH} />}
            </header>
            <main className="flex-1">
                <div className={cn(SHELL_WIDTH, 'py-8 sm:py-10')}>{children}</div>
            </main>
        </div>
    )
}
